import React from "react";
import { MapPin, ArrowUp, Phone, Mail, Youtube, Globe } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { ASSETS } from "../../lib/assets";
import { COMPANY } from "../../lib/data";
import { MPattern } from "./MPattern";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Footer = () => {
  const { t, lang } = useLang();
  const links = [
    ["services", t.nav.services],
    ["equipment", t.nav.equipment],
    ["certifications", t.nav.certifications],
    ["why", t.nav.why],
    ["waterproofing", t.nav.waterproofing],
    ["contact", t.nav.contact],
  ];
  return (
    <footer className="relative bg-brand-navy overflow-hidden" data-testid="footer">
      <MPattern opacity={0.04} color="#3C8DD0" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand + contact */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={ASSETS.brandSymbol} alt="Metal Covering" className="h-10 w-10 object-contain" />
              <span className="font-display text-xl font-semibold tracking-wide text-white">
                METAL <span className="text-brand-azure">COVERING</span>
              </span>
            </div>
            <p className="mt-4 font-condensed text-xs uppercase tracking-[0.18em] text-brand-azure">{COMPANY.descriptor}</p>
            <p className="mt-4 max-w-sm text-slate-400 text-sm leading-relaxed">{t.footer.tagline}</p>
            <div className="mt-6 space-y-2 text-sm text-slate-300">
              <a href={`tel:${COMPANY.telHref}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-tel">
                <Phone size={15} className="text-brand-azure" /> {COMPANY.tel}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email">
                <Mail size={15} className="text-brand-azure" /> {COMPANY.email}
              </a>
              <a href={COMPANY.webHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-web">
                <Globe size={15} className="text-brand-azure" /> {COMPANY.web}
              </a>
              <a href={COMPANY.youtube} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-youtube">
                <Youtube size={16} className="text-brand-azure" /> YouTube
              </a>
            </div>
          </div>

          {/* Nav */}
          <div className="lg:col-span-3">
            <h4 className="font-condensed text-xs uppercase tracking-[0.2em] text-brand-azure">{t.footer.quickLinks}</h4>
            <ul className="mt-5 space-y-3">
              {links.map(([id, label]) => (
                <li key={id}>
                  <button onClick={() => scrollTo(id)} className="text-slate-300 hover:text-white text-sm transition-colors link-underline" data-testid={`footer-link-${id}`}>
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Offices */}
          <div className="lg:col-span-5">
            <h4 className="font-condensed text-xs uppercase tracking-[0.2em] text-brand-azure">{t.footer.locationsTitle}</h4>
            <div className="mt-5 grid sm:grid-cols-2 gap-6 text-sm">
              <div data-testid="office-registered">
                <div className="flex items-center gap-2 text-white font-medium"><MapPin size={14} className="text-brand-azure" /> {COMPANY.registered[lang]}</div>
                <div className="mt-1.5 text-slate-400 leading-relaxed">{COMPANY.registered.lines[0]}<br />{COMPANY.registered.lines[1]}</div>
              </div>
              <div data-testid="office-lainate">
                <div className="flex items-center gap-2 text-white font-medium"><MapPin size={14} className="text-brand-azure" /> {COMPANY.lainate[lang]}</div>
                <div className="mt-1.5 text-slate-400 leading-relaxed">{COMPANY.lainate.lines[0]}<br />{COMPANY.lainate.lines[1]}</div>
              </div>
            </div>
            <div className="mt-6" data-testid="office-temporary">
              <div className="font-condensed text-[11px] uppercase tracking-[0.15em] text-slate-500">{COMPANY.temporary[lang]}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {COMPANY.temporary.items.map((o, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-azure" /> {o.name} — {o.place}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            © {new Date().getFullYear()} {COMPANY.legalName}. {t.footer.rights}<br />
            P.IVA / C.F. {COMPANY.vat} · SDI {COMPANY.sdi} · {COMPANY.rea}
          </p>
          <button
            onClick={() => scrollTo("hero")}
            className="inline-flex items-center gap-2 text-xs font-condensed uppercase tracking-[0.15em] text-slate-400 hover:text-white transition-colors shrink-0"
            data-testid="footer-back-top"
          >
            <ArrowUp size={14} /> Top
          </button>
        </div>
      </div>
    </footer>
  );
};
